import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, Link } from "react-router-dom";
import { LogOut, PanelRightClose, PanelRightOpen, Menu, X } from "lucide-react";
import { useAuth, homeFor } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Logo, LangSwitch, Loader } from "./ui";
import { LangMenu } from "./PublicLayout";

const MobileHeader = ({ nav, logout }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <header className="md:hidden sticky top-0 z-30 bg-black/90 backdrop-blur-md border-b border-white/10" data-testid="portal-mobile-header">
      <div className="flex items-center justify-between px-4 h-16">
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen(!open)} className="h-11 w-11 grid place-items-center" aria-label="menu" aria-expanded={open} data-testid="portal-menu-button">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <LangMenu />
        </div>
        <Logo className="h-9" testid="portal-logo-mobile" />
      </div>
      {open && (
        <nav className="border-t border-white/5 bg-black px-3 py-3 max-h-[calc(100svh-4rem)] overflow-y-auto" data-testid="portal-mobile-menu">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-3.5 text-base border-s-2 ${isActive ? "border-[var(--xd-red)] text-white bg-white/[0.04]" : "border-transparent text-zinc-400"}`} data-testid={`nav-m-${n.id}`}>
              {n.icon}{n.label}{n.badge > 0 && <span className="ms-auto min-w-[1.5rem] h-6 px-1.5 grid place-items-center rounded-full bg-[var(--xd-red)] text-white text-xs ltr-num" data-testid={`nav-m-${n.id}-badge`}>{n.badge}</span>}
            </NavLink>
          ))}
          <div className="mt-2 pt-3 border-t border-white/10 flex items-center justify-between px-3">
            <span className="text-xs text-zinc-500 ltr-num">{user?.uid}</span>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-zinc-300 py-2" data-testid="logout-button-mobile"><LogOut size={16} />{t("auth.logout")}</button>
          </div>
        </nav>
      )}
    </header>
  );
};

const navCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap transition-colors border-s-2 ${isActive ? "border-[var(--xd-red)] bg-white/5 text-white" : "border-transparent text-zinc-400 hover:text-white"}`;

const UserBox = ({ roleLabel, collapsed }) => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  if (collapsed) {
    return (
      <div className="p-3 border-t border-white/10 grid place-items-center gap-2">
        <span className="text-[10px] text-zinc-500 ltr-num" title={`${user?.name} · ${roleLabel}`} data-testid="portal-user-uid">{user?.uid}</span>
        <button onClick={() => setLang(lang === "he" ? "ar" : "he")} className="h-9 w-10 grid place-items-center border border-white/15 text-xs text-zinc-300 hover:text-white" data-testid="portal-lang-toggle-collapsed">{lang === "he" ? "ع" : "ע"}</button>
        <button onClick={logout} className="h-10 w-10 grid place-items-center text-zinc-400 hover:text-white" title={t("auth.logout")} data-testid="logout-button"><LogOut size={16} /></button>
      </div>
    );
  }
  return (
    <div className="p-4 border-t border-white/10 space-y-3">
      <div>
        <p className="text-sm text-white truncate">{user?.name}</p>
        <p className="text-xs text-zinc-500"><span className="ltr-num" data-testid="portal-user-uid">{user?.uid || "—"}</span> · {roleLabel}</p>
      </div>
      <div className="flex items-center gap-2">
        <LangSwitch testid="portal-lang" />
        <button onClick={logout} className="btn-ghost !min-h-[2.25rem] !px-3 text-xs" data-testid="logout-button"><LogOut size={14} />{t("auth.logout")}</button>
      </div>
    </div>
  );
};

export const PortalShell = ({ nav, roleLabel }) => {
  const { logout } = useAuth();
  const { t } = useLang();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("xd_sidebar") === "1");
  const toggle = () => { localStorage.setItem("xd_sidebar", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  return (
    <div className="min-h-screen bg-black text-white md:flex">
      <aside className={`hidden md:flex ${collapsed ? "md:w-[72px]" : "md:w-64"} shrink-0 flex-col border-e border-white/10 bg-[#050505] sticky top-0 h-screen transition-[width] duration-300`} data-testid="portal-sidebar" data-collapsed={collapsed}>
        <div className={`flex items-center ${collapsed ? "justify-center p-3" : "justify-between p-6 pe-3"}`}>
          {!collapsed && <Link to="/" className="block"><Logo className="h-14" testid="portal-logo" /></Link>}
          <button onClick={toggle} className="h-10 w-10 grid place-items-center border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 transition-colors"
            title={t(collapsed ? "portal.expand" : "portal.collapse")} aria-label={t(collapsed ? "portal.expand" : "portal.collapse")} data-testid="sidebar-toggle-button">
            {collapsed ? <PanelRightOpen size={16} /> : <PanelRightClose size={16} />}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto overflow-x-hidden space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={(s) => `${navCls(s)} ${collapsed ? "justify-center !px-0" : ""}`} title={collapsed ? n.label : undefined} data-testid={`nav-${n.id}`}>
              <span className="relative">{n.icon}{collapsed && n.badge > 0 && <span className="absolute -top-1.5 -end-1.5 h-2.5 w-2.5 rounded-full bg-[var(--xd-red)]" />}</span>
              {!collapsed && n.label}
              {!collapsed && n.badge > 0 && <span className="ms-auto min-w-[1.5rem] h-6 px-1.5 grid place-items-center rounded-full bg-[var(--xd-red)] text-white text-xs ltr-num" data-testid={`nav-${n.id}-badge`}>{n.badge}</span>}
            </NavLink>
          ))}
        </nav>
        <UserBox roleLabel={roleLabel} collapsed={collapsed} />
      </aside>
      <div className="flex-1 min-w-0">
        <MobileHeader nav={nav} logout={logout} />
        <main className={`px-4 py-6 md:px-10 md:py-10 ${collapsed ? "max-w-[1600px]" : "max-w-7xl"}`}><Outlet /></main>
      </div>
    </div>
  );
};

const PendingScreen = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  return (
    <div className="min-h-screen grid place-items-center bg-black px-6 text-center" data-testid="pending-screen">
      <div className="max-w-md space-y-6">
        <Logo className="h-24 mx-auto" testid="pending-logo" />
        <h1 className="text-2xl chrome-text font-semibold">{t("pending.title")}</h1>
        <p className="text-zinc-400">{t("pending.text")}</p>
        <div className="flex justify-center gap-3"><LangSwitch /><button className="btn-ghost" onClick={logout} data-testid="pending-logout">{t("auth.logout")}</button></div>
        <p className="text-xs text-zinc-600 ltr-num">{user?.phone}</p>
      </div>
    </div>
  );
};

export const Protected = ({ role }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={`/login?as=${role}`} replace />;
  if (user.role !== role) return <Navigate to={homeFor(user)} replace />;
  if (user.status !== "approved") return <PendingScreen />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  const guidePath = { broker: "/broker/rules", dealer: "/dealer/guide" }[role];
  if (guidePath && user.guide_version !== user.guide_current && loc.pathname !== guidePath) return <Navigate to={guidePath} replace />;
  return <Outlet />;
};
